import React from 'react';
import { Route, useLocation } from 'react-router-dom';
import Layout from './components/Layout/Layout';
import CatalogManagement from './pages/Settings/CatalogManagement';
import EmbeddedExtensionRedirect from './pages/EmbeddedExtensionRedirect';
import EmbeddedProductPage from './pages/EmbeddedProduct';
import EmbeddedProductLinkPage from './pages/EmbeddedProductLinkPage';
import ExtensionManagement from './pages/ExtensionManagement';
import FolioEditorPage from './pages/Settings/FolioEditorPage';
import Home from './pages/Home';
import SearchPage from './pages/Search';
import WorkspacePage from './pages/WorkspacePage';
import QuickOpportunityImportPage from './pages/QuickOpportunityImportPage';
import EmbeddedPunchoutPage from "./pages/EmbeddedPunchout";
import SessionKeyLayout from "./components/SessionKeyLayout/SessionKeyLayout";
import ApiKeyManagementPage from "./pages/ApiKeyManagementPage";
import SettingsLayout from "./components/SettingsLayout/SettingsLayout";
import UserManagementPage from "./pages/Settings/UserManagementPage";
import SearchReportingPage from "./pages/Reporting/SearchReportingPage";
import ReportingLayout from "./components/ReportingLayout/ReportingLayout";
import TaxCodeManagement from "./pages/Settings/TaxCodeManagement";
import SubOrganizationManagement from "./pages/Settings/SubOrganizationManagement";
import OrganizationManagement from "./pages/Settings/OrganizationManagement";
import SettingsHomePage from "./pages/Settings/SettingsHomePage";
import ApiUsageReport from "./pages/Settings/Reporting/ApiUsageReport";
import AuthorizedRoute from "./AuthorizedRoutes"
import SupportLoginPage from "./pages/Auth/SupportLoginPage";
import {SUPPORT_LOGIN_PATH} from "./logic/supportSession";
import EmbeddedLogin from "./pages/Auth/EmbeddedLogin";
import SearchSettingsPage from './pages/Settings/SearchSettingsPage';
import StorefrontManagementPage from './pages/Settings/StorefrontManagementPage';
import ContactManagementPage from './pages/Settings/ContactManagementPage';
import VendorManagementPage from './pages/Settings/VendorManagementPage';
import ScrollToTop from './components/ScrollToTop';
import CategoryManagementPage from "./pages/Settings/CategoryManagement";
import WebhooksPage from './pages/Settings/WebhooksPage';
import ProcurementHomePage from './pages/Procurement/ProcurementHomePage';
import ProcurementManagementPage from './pages/Settings/ProcurementManagement';
import ProcurementOrdersPage from './pages/Procurement/ProcurementOrdersPage';
import ProcurementDemandItemsPage from './pages/Procurement/ProcurementDemandItemsPage';
import UserDetailsPage from "./pages/Settings/UserDetailsPage";
import StorefrontDashboardPage from './pages/Settings/Storefront/StorefrontLevel/StorefrontDashboardPage';
import StorefrontGeneralSettingsPage from './pages/Settings/Storefront/StorefrontLevel/StorefrontGeneralSettingsPage';
import StorefrontLocalizationPage from './pages/Settings/Storefront/StorefrontLevel/StorefrontLocalizationPage';
import StorefrontGlobalDashboardPage from './pages/Settings/Storefront/Global/StorefrontGlobalDashboardPage';
import StorefrontListPage from './pages/Settings/Storefront/Global/StorefrontListPage';
import StorefrontGlobalOrderingAndCartsSettings from './pages/Settings/Storefront/Global/StorefrontGlobalOrderingAndCartsSettings';
import StorefrontPagesEditorPage from './pages/Settings/Storefront/StorefrontPagesEditorPage';
import StorefrontGlobalProductSettingsPage from './pages/Settings/Storefront/Global/StorefrontGlobalProductSettingsPage';
import StorefrontGlobalIntegrationsPage from './pages/Settings/Storefront/Global/StorefrontGlobalIntegrationsPage';
import StorefrontOrderingSettingsPage from './pages/Settings/Storefront/StorefrontLevel/StorefrontOrderingSettingsPage';
import StorefrontProductsPage from './pages/Settings/Storefront/StorefrontLevel/StorefrontProductsPage';
import StorefrontIntegrationsPage from './pages/Settings/Storefront/StorefrontLevel/StorefrontIntegrationsPage';
import StorefrontAuthenticationSettingsPage from './pages/Settings/Storefront/StorefrontLevel/StorefrontAuthenticationSettingsPage';
import StorefrontUsersPage from './pages/Settings/Storefront/StorefrontLevel/StorefrontUsersPage';
import StorefrontGlobalAppearancePage from './pages/Settings/Storefront/Global/StorefrontGlobalAppearancePage';
import StorefrontAssetsPage from './pages/Settings/Storefront/Global/StorefrontAssetsPage';
import SettingDeepLinkResolver from './pages/Settings/SettingDeepLinkResolver';
import OnboardingLinkResolver from './pages/OnboardingLinkResolver';
import SettingsReferencePage from './pages/Settings/SettingsReferencePage';
import StorefrontDashboardQuickAccessPage from './pages/Storefront/StorefrontDashboardPage';
import StorefrontOrdersQuickAccessPage from './pages/Storefront/StorefrontOrdersPage';
import StorefrontCartsQuickAccessPage from './pages/Storefront/StorefrontCartsPage';
export const Router: React.FC = () => {
    const location = useLocation();

    React.useEffect(() => {
        if (window.Beacon) {
            window.Beacon('event', {
                type: 'page-viewed',
                url: document.location.href,
                title: document.title,
            });
            window.Beacon('suggest');
        }
    }, [location]);

    return (
        <>
            <ScrollToTop />
            {/*
             * '/' shares this Route (and its single <Layout>) with everything else rather than
             * getting its own - it used to be a separate <Route path='/' exact> with its own
             * <Layout>. Since neither Route is inside a <Switch>, React Router mounted whichever
             * one matched: navigating between '/' and any other path here (e.g. the bundle editor's
             * area, which is deliberately routed to '/' - see registry/areas.ts 'bundles.questions')
             * unmounted one <Layout> and mounted the other. <SettingFocusHost> lives inside
             * <Layout>, so a focus resolution in flight was torn down mid-wait with no toast ever
             * firing - the "never rings and never toasts" bundle-question bug. One shared <Layout>
             * for every top-level path removes the remount entirely.
             */}
            <Route path={['/', '/search', '/workspace', '/settings*', '/reporting/*', '/procurement*', '/storefront*', '/go/*']}>
                <Layout>
                    <AuthorizedRoute exact path="/" component={Home}/>
                    <AuthorizedRoute path="/search" component={SearchPage}/>
                    {/* Stable links for documentation. These resolve to a real settings URL and
                        replace themselves in the history, so Back skips them. */}
                    <AuthorizedRoute path='/go/setting/:settingId' component={SettingDeepLinkResolver}/>
                    <AuthorizedRoute path='/go/area/:areaKey' component={SettingDeepLinkResolver}/>
                    <AuthorizedRoute path='/go/onboarding/:milestone' component={OnboardingLinkResolver}/>
                    {/* PSA quote-link quick flow: imports the opportunity and lands on the prepared cart. */}
                    <AuthorizedRoute path='/go/opportunity-import/:extensionId' component={QuickOpportunityImportPage}/>
                    <AuthorizedRoute path="/Workspace/:workspaceId" component={WorkspacePage}/>
                    <AuthorizedRoute path='/settings' exact component={SettingsHomePage} />
                    <Route path={'/settings/*'}>
                        <SettingsLayout>
                            <AuthorizedRoute path='/settings/organization' component={OrganizationManagement} />
                            <AuthorizedRoute path="/settings/catalogs" component={CatalogManagement}/>
                            <AuthorizedRoute path="/settings/categories" component={CategoryManagementPage}/>
                            <AuthorizedRoute path="/settings/extensions" component={ExtensionManagement}/>
                            <AuthorizedRoute path="/settings/folios" component={FolioEditorPage}/>
                            <AuthorizedRoute path='/settings/apikeys' component={ApiKeyManagementPage}/>
                            <AuthorizedRoute path='/settings/users' component={UserManagementPage}/>
                            <AuthorizedRoute path='/settings/myUser' component={UserDetailsPage}/>
                            <AuthorizedRoute path='/settings/taxcodes' component={TaxCodeManagement}/>
                            <AuthorizedRoute path='/settings/subOrganizations' component={SubOrganizationManagement} />
                            <AuthorizedRoute path='/settings/reporting/api' component={ApiUsageReport} />
                            <AuthorizedRoute path='/settings/search' component={SearchSettingsPage} />
                            <AuthorizedRoute path='/settings/reference' component={SettingsReferencePage} />
                            <AuthorizedRoute path='/settings/storefront' component={StorefrontManagementPage} exact />
                            <AuthorizedRoute path='/settings/contacts' component={ContactManagementPage} />
                            <AuthorizedRoute path='/settings/vendors' component={VendorManagementPage} />
                            <AuthorizedRoute path='/settings/webhooks' component={WebhooksPage} />
                            <AuthorizedRoute path='/settings/procurement' component={ProcurementManagementPage} />
                            <AuthorizedRoute path='/settings/storefront/dashboard' component={StorefrontGlobalDashboardPage} exact />
                            <AuthorizedRoute path='/settings/storefront/ordering' component={StorefrontGlobalOrderingAndCartsSettings} exact />
                            <AuthorizedRoute path='/settings/storefront/products' component={StorefrontGlobalProductSettingsPage} exact />
                            <AuthorizedRoute path='/settings/storefront/integrations' component={StorefrontGlobalIntegrationsPage} exact />
                            <AuthorizedRoute path='/settings/storefront/appearance' component={StorefrontGlobalAppearancePage} exact />
                            <AuthorizedRoute path='/settings/storefront/assets' component={StorefrontAssetsPage} exact />
                            <AuthorizedRoute path='/settings/storefront/storefronts' component={StorefrontListPage} exact />
                            <AuthorizedRoute path='/settings/storefront/storefronts/:storefront/dashboard' component={StorefrontDashboardPage} exact />
                            <AuthorizedRoute path='/settings/storefront/storefronts/:storefront/pages' component={StorefrontPagesEditorPage} exact />
                            <AuthorizedRoute path='/settings/storefront/storefronts/:storefront/general' component={StorefrontGeneralSettingsPage} exact />
                            <AuthorizedRoute path='/settings/storefront/storefronts/:storefront/localization' component={StorefrontLocalizationPage} exact />
                            <AuthorizedRoute path='/settings/storefront/storefronts/:storefront/ordering' component={StorefrontOrderingSettingsPage} exact />
                            <AuthorizedRoute path='/settings/storefront/storefronts/:storefront/products' component={StorefrontProductsPage} exact />
                            <AuthorizedRoute path='/settings/storefront/storefronts/:storefront/integrations' component={StorefrontIntegrationsPage} exact />
                            <AuthorizedRoute path='/settings/storefront/storefronts/:storefront/authentication' component={StorefrontAuthenticationSettingsPage} />
                            <AuthorizedRoute path='/settings/storefront/storefronts/:storefront/users' component={StorefrontUsersPage} />
                        </SettingsLayout>
                    </Route>
                    <AuthorizedRoute path='/procurement' component={ProcurementHomePage} exact />
                    <Route path={'/procurement/*'}>
                        <AuthorizedRoute path="/procurement/orders" component={ProcurementOrdersPage}/>
                        <AuthorizedRoute path='/procurement/demands/items' component={ProcurementDemandItemsPage} />
                    </Route>
                    <AuthorizedRoute path='/storefront' component={StorefrontDashboardQuickAccessPage} exact />
                    <Route path={'/storefront/*'}>
                        <AuthorizedRoute path='/storefront/orders' component={StorefrontOrdersQuickAccessPage} />
                        <AuthorizedRoute path='/storefront/carts' component={StorefrontCartsQuickAccessPage} />
                    </Route>
                    <Route path={'/reporting/*'}>
                        <ReportingLayout>
                            <AuthorizedRoute path='/reporting/search/usage' component={SearchReportingPage} />
                        </ReportingLayout>
                    </Route>
                </Layout>
            </Route>

            <Route path={SUPPORT_LOGIN_PATH} exact component={SupportLoginPage}/>

            <Route path='/embedded/*'>
                <Route path='/embedded/login' component={EmbeddedLogin}/>
                <Route path='/embedded/product/:catalog/:id' component={EmbeddedProductPage}/>
                <Route path='/embedded/extension/:extensionId' component={EmbeddedExtensionRedirect}/>
                <Route path='/embedded/embedded/establishLink/product/:extensionId/:id'
                             component={EmbeddedProductLinkPage}/>
            </Route>

            <Route path={['/punchout', '/punchout/*']}>
                <SessionKeyLayout>
                    <Route path='/punchout' exact component={EmbeddedPunchoutPage}/>
                    <Route path='/punchout/search' component={SearchPage}/>
                    <Route path='/punchout/workspace/:workspaceId' component={WorkspacePage}/>
                </SessionKeyLayout>
            </Route>
        </>
    )
}